import { z } from 'zod';
import { User } from '../user';
import { FBCallToAction } from './AdCreative';

/**
 * What changed in one copy save, field by field, with the value before and after.
 *
 * Deliberately not a comment: comments are client-readable, editable by their
 * author, and feed unresolved counts and notifications. This is an append-only
 * record of what a strategist changed, read only by employees.
 *
 * Zod rather than a plain type because this is stored schemaless (one jsonb
 * column) — reading it back is a runtime boundary, and a row written by an older
 * shape has to fail loudly rather than render as undefined.
 */

/** Copy that belongs to the ad itself, whatever its format. */
export const creativeCopyAdFieldSchema = z.enum([
  'primaryText',
  'headline',
  'description',
  'callToAction',
  'url',
]);

export type CreativeCopyAdField = z.infer<typeof creativeCopyAdFieldSchema>;

/** Copy that belongs to one carousel card. */
export const creativeCopySlideFieldSchema = z.enum([
  'headline',
  'description',
  'url',
]);

export type CreativeCopySlideField = z.infer<
  typeof creativeCopySlideFieldSchema
>;

/** A dynamic ad's copy pools, which Facebook mixes at delivery. */
export const creativeCopyPoolNameSchema = z.enum([
  'primaryTexts',
  'headlines',
  'descriptions',
  'callToActions',
]);

export type CreativeCopyPoolName = z.infer<typeof creativeCopyPoolNameSchema>;

// A missing scalar is null, never undefined: `''` has to stay distinct, because
// clearing a populated headline is a real edit and must not read as "absent".
const scalarValueSchema = z.string().nullable();

/** Which media collection a change landed in. */
export const creativeMediaWhereSchema = z.enum(['single', 'card', 'asset']);

export type CreativeMediaWhere = z.infer<typeof creativeMediaWhereSchema>;

export const creativeMediaActionSchema = z.enum(['replace', 'add', 'remove']);

export type CreativeMediaAction = z.infer<typeof creativeMediaActionSchema>;

export const creativeCopyChangeSchema = z.discriminatedUnion('scope', [
  z.object({
    scope: z.literal('ad'),
    field: creativeCopyAdFieldSchema,
    before: scalarValueSchema,
    after: scalarValueSchema,
  }),
  z.object({
    scope: z.literal('slide'),
    slideID: z.number().int().positive(),
    /**
     * One-based, and the number the strategist saw on screen. Stored rather than
     * derived so the feed can still say "Card 2" after that card is gone.
     */
    position: z.number().int().positive(),
    field: creativeCopySlideFieldSchema,
    before: scalarValueSchema,
    after: scalarValueSchema,
  }),
  z.object({
    scope: z.literal('pool'),
    pool: creativeCopyPoolNameSchema,
    // A whole pool, compared as a whole: a reordering is a change.
    before: z.array(z.string()),
    after: z.array(z.string()),
  }),
  z.object({
    scope: z.literal('media'),
    where: creativeMediaWhereSchema,
    action: creativeMediaActionSchema,
    /**
     * The card's one-based position, for a carousel change only. Stored rather
     * than derived, and for a removal it is the position the card had — which is
     * the only number that still names it afterwards.
     */
    position: z.number().int().positive().optional(),
    // A human file label rather than a type/id pair: the row is read by a
    // strategist recalling which video they swapped in, not by code.
    before: scalarValueSchema,
    after: scalarValueSchema,
  }),
]);

export type CreativeCopyChange = z.infer<typeof creativeCopyChangeSchema>;

export const creativeCopyChangesSchema = z.array(creativeCopyChangeSchema);

/**
 * One copy save, as the history feed reads it. An ordinary interface rather than
 * a second schema: nothing parses the envelope, only the stored change set.
 *
 * `author` is null when the user has been deleted, mirroring `Comment` — the
 * feed already renders "Deleted user".
 */
export interface CreativeCopyEditEvent {
  id: number;
  creativeID: number;
  author: User | null;
  changes: CreativeCopyChange[];
  createdAt: string;
}

export const exampleCreativeCopyChange: CreativeCopyChange = {
  scope: 'ad',
  field: 'headline',
  before: 'Old headline',
  after: 'New headline',
};

export const exampleCreativeCopyPoolChange: CreativeCopyChange = {
  scope: 'pool',
  pool: 'callToActions',
  before: [FBCallToAction.LEARN_MORE],
  after: [FBCallToAction.SIGN_UP],
};
